import type { DealFormData } from '@openx/types/deal';
import { microToPercentWithPrecision } from '@openx/utils/lib/microToPercent';
import { type Path, useWatch } from 'react-hook-form';
import { calculateNetFee } from '../utils/calculateNetFee';
import { useIsCpmRevenue } from './useIsCpmRevenue';

export const useNetFeeCalculation = (defaultPlatformShare: null | string | undefined, index: number) => {
  const grossCpmCapFieldName: Path<DealFormData> = `third_party_fees_config.${index}.gross_cpm_cap`;
  const grossShareFieldName: Path<DealFormData> = `third_party_fees_config.${index}.gross_share`;
  const platformShareFieldName: Path<DealFormData> = `third_party_fees_config.${index}.platform_share`;

  const observableGrossCpmCap = useWatch<DealFormData, typeof grossCpmCapFieldName>({
    name: grossCpmCapFieldName,
  });

  const observableGrossShare = useWatch<DealFormData, typeof grossShareFieldName>({
    name: grossShareFieldName,
  });

  const observablePlatformShare = useWatch<DealFormData, typeof platformShareFieldName>({
    name: platformShareFieldName,
  });

  const isCpmRevenue = useIsCpmRevenue(index);
  const observableGrossCpmCapNumeric = Number.parseFloat(observableGrossCpmCap);
  const observableGrossShareNumeric = Number.parseFloat(observableGrossShare);

  if (isCpmRevenue && (!observableGrossCpmCap || observableGrossCpmCapNumeric < 0)) {
    return null;
  }

  if (!isCpmRevenue && (!observableGrossShare || observableGrossShareNumeric < 0)) {
    return null;
  }

  const platformShare = (observablePlatformShare || microToPercentWithPrecision(defaultPlatformShare)) ?? '0';
  const platformShareNumeric = Number.parseFloat(platformShare);

  if (platformShareNumeric < 0) {
    return null;
  }

  // `gross_cpm_cap` is rounded to 2 decimal places as it represents a currency
  // `gross_share` is rounded to 1 decimal places as it represents a percentage
  const netFee = isCpmRevenue
    ? calculateNetFee(observableGrossCpmCapNumeric, platformShareNumeric, 2)
    : calculateNetFee(observableGrossShareNumeric, platformShareNumeric, 1);

  return Number.isNaN(Number.parseFloat(netFee)) ? null : netFee;
};
